import type { ReactNode, SVGProps } from 'react';
import type { AllergenCode } from './allergens';

/** SC-1280 — les 14 pictogrammes du registre HACCP (`ouitap-haccp`, viewBox 48),
 *  portés en React. Traits en `currentColor` ; la teinte d'accent des originaux
 *  (`--icon-accent`) devient un aplat `currentColor` à 25 % : à 14–16 px une
 *  couleur de plus ne se lit pas, un voile de la couleur du texte oui. */
const ACCENT = { fill: 'currentColor', fillOpacity: 0.25 } as const;

const SHAPES: Record<AllergenCode, () => ReactNode> = {
  gluten: () => (
    <>
      <path d="M24 44V14" />
      <path {...ACCENT} stroke="none" d="M24 37c-4.8 0-8-3.2-8-8 4.8 0 8 3.2 8 8zM24 37c4.8 0 8-3.2 8-8-4.8 0-8 3.2-8 8z" />
      <path d="M24 37c-4.8 0-8-3.2-8-8 4.8 0 8 3.2 8 8zM24 37c4.8 0 8-3.2 8-8-4.8 0-8 3.2-8 8z" />
      <path {...ACCENT} stroke="none" d="M24 29c-4.8 0-8-3.2-8-8 4.8 0 8 3.2 8 8zM24 29c4.8 0 8-3.2 8-8-4.8 0-8 3.2-8 8z" />
      <path d="M24 29c-4.8 0-8-3.2-8-8 4.8 0 8 3.2 8 8zM24 29c4.8 0 8-3.2 8-8-4.8 0-8 3.2-8 8z" />
      <path {...ACCENT} stroke="none" d="M24 21c-4.8 0-8-3.2-8-8 4.8 0 8 3.2 8 8zM24 21c4.8 0 8-3.2 8-8-4.8 0-8 3.2-8 8z" />
      <path d="M24 21c-4.8 0-8-3.2-8-8 4.8 0 8 3.2 8 8zM24 21c4.8 0 8-3.2 8-8-4.8 0-8 3.2-8 8z" />
      <path {...ACCENT} stroke="none" d="M24 14c-2.3-2.3-2.3-6.5 0-9.3 2.3 2.8 2.3 7 0 9.3z" />
      <path d="M24 14c-2.3-2.3-2.3-6.5 0-9.3 2.3 2.8 2.3 7 0 9.3z" />
      <path d="M17.5 13 13 5M30.5 13 35 5" />
    </>
  ),
  crustaceans: () => (
    <>
      <path {...ACCENT} stroke="none" d="M11 30c0-6 5.8-10 13-10s13 4 13 10-5.8 9-13 9-13-3-13-9z" />
      <path d="M11 30c0-6 5.8-10 13-10s13 4 13 10-5.8 9-13 9-13-3-13-9z" />
      <path d="M20.5 20.5V16M27.5 20.5V16" />
      <circle fill="currentColor" stroke="none" cx={20.5} cy={14.5} r={1.9} />
      <circle fill="currentColor" stroke="none" cx={27.5} cy={14.5} r={1.9} />
      <path d="M13.5 23.5 10 17M34.5 23.5 38 17" />
      <path d="M10.2 17.3a5.4 5.4 0 1 1 4.2-6.4L10 12.4" />
      <path d="M37.8 17.3a5.4 5.4 0 1 0-4.2-6.4l4.4 1.5" />
      <path d="M11.5 32 5.5 35M12.5 36l-5 5M16 38.5l-2.5 5M36.5 32l6 3M35.5 36l5 5M32 38.5l2.5 5" />
    </>
  ),
  eggs: () => (
    <>
      <path {...ACCENT} stroke="none" d="M24 5.5c6.6 0 12 10 12 19.5a12 12 0 0 1-24 0C12 15.5 17.4 5.5 24 5.5z" />
      <path d="M24 5.5c6.6 0 12 10 12 19.5a12 12 0 0 1-24 0C12 15.5 17.4 5.5 24 5.5z" />
      <path d="M18.5 25c.2-4.2 1.8-8 3.8-10.5" />
    </>
  ),
  fish: () => (
    <>
      <path {...ACCENT} stroke="none" d="M12 24c4.2-6.5 10-10 17-10s11.3 3.5 13.5 10c-2.2 6.5-6.5 10-13.5 10S16.2 30.5 12 24z" />
      <path d="M12 24c4.2-6.5 10-10 17-10s11.3 3.5 13.5 10c-2.2 6.5-6.5 10-13.5 10S16.2 30.5 12 24z" />
      <path d="M12 24 4.5 17.5v13z" />
      <circle fill="currentColor" stroke="none" cx={35.5} cy={21.5} r={1.9} />
      <path d="M29.5 16.5c1.8 4.6 1.8 10.4 0 15" />
    </>
  ),
  peanuts: () => (
    <>
      <g transform="rotate(35 24 24)">
        <path {...ACCENT} stroke="none" d="M24 6c4.7 0 8 3.4 8 8 0 3.1-1.6 4.9-1.6 7.3 0 2.2 3.1 4 3.1 9 0 5.6-4.3 9.7-9.5 9.7s-9.5-4.1-9.5-9.7c0-5 3.1-6.8 3.1-9 0-2.4-1.6-4.2-1.6-7.3 0-4.6 3.3-8 8-8z" />
        <path d="M24 6c4.7 0 8 3.4 8 8 0 3.1-1.6 4.9-1.6 7.3 0 2.2 3.1 4 3.1 9 0 5.6-4.3 9.7-9.5 9.7s-9.5-4.1-9.5-9.7c0-5 3.1-6.8 3.1-9 0-2.4-1.6-4.2-1.6-7.3 0-4.6 3.3-8 8-8z" />
        <path d="M20.5 13h.01M27.5 13.5h.01M24 17.5h.01M20 30h.01M28 29h.01M24 34.5h.01M21 24.5c2 .8 4 .8 6 0" />
      </g>
    </>
  ),
  soy: () => (
    <>
      <g transform="rotate(-38 24 24)">
        <rect x={5} y={16} width={36} height={16} rx={8} />
        <circle {...ACCENT} stroke="none" cx={14} cy={24} r={4.3} />
        <circle {...ACCENT} stroke="none" cx={23} cy={24} r={4.3} />
        <circle {...ACCENT} stroke="none" cx={32} cy={24} r={4.3} />
        <circle cx={14} cy={24} r={4.3} strokeWidth={3} />
        <circle cx={23} cy={24} r={4.3} strokeWidth={3} />
        <circle cx={32} cy={24} r={4.3} strokeWidth={3} />
        <path d="M41 24h2.5a2 2 0 0 0 2-2" />
      </g>
    </>
  ),
  milk: () => (
    <>
      <path {...ACCENT} stroke="none" d="M14 29c3.3-2 6.7-2 10 0s6.7 2 10 0v11a3 3 0 0 1-3 3H17a3 3 0 0 1-3-3z" />
      <path d="M14 19l5-8h10l5 8v21a3 3 0 0 1-3 3H17a3 3 0 0 1-3-3z" />
      <path d="M14 19h20M19 11V5.5h10V11" />
    </>
  ),
  nuts: () => (
    <>
      <path {...ACCENT} stroke="none" d="M14.2 24A11 11 0 1 0 33.8 24z" />
      <path d="M14.2 24A11 11 0 1 0 33.8 24" />
      <path d="M11.5 24.5c0-7.3 5.6-12.5 12.5-12.5s12.5 5.2 12.5 12.5" />
      <path d="M11.5 24.5l3.1-2.2 3.1 2.2 3.1-2.2 3.1 2.2 3.1-2.2 3.1 2.2 3.1-2.2 3.1 2.2" />
      <path d="M24 12V6.5" />
    </>
  ),
  celery: () => (
    <>
      <path {...ACCENT} stroke="none" d="M17.5 43l4-23h5l4 23z" />
      <path d="M17.5 43l4-23M30.5 43l-4-23M24 43V20M16 43h16" />
      <path d="M21.5 20c-4-.8-6.8-4.2-6-8.8 4 .5 6.6 4 6 8.8zM24 20c-2.6-3-2.6-8 0-12.2 2.6 4.2 2.6 9.2 0 12.2zM26.5 20c4-.8 6.8-4.2 6-8.8-4 .5-6.6 4-6 8.8z" />
    </>
  ),
  mustard: () => (
    <>
      <path {...ACCENT} stroke="none" d="M13 22h22v16a5 5 0 0 1-5 5H18a5 5 0 0 1-5-5z" />
      <path d="M13 22h22v16a5 5 0 0 1-5 5H18a5 5 0 0 1-5-5z" />
      <rect x={11} y={16} width={26} height={6} rx={2} />
      <path d="M29 16l6.5-10.5" />
      <circle fill="currentColor" stroke="none" cx={19} cy={30} r={1.8} />
      <circle fill="currentColor" stroke="none" cx={24} cy={34} r={1.8} />
      <circle fill="currentColor" stroke="none" cx={29} cy={30} r={1.8} />
    </>
  ),
  sesame: () => (
    <>
      <g transform="translate(13 20) rotate(-35)">
        <path {...ACCENT} stroke="none" d="M0 -7c2.9 2.3 4 5 4 7.3a4 4 0 0 1-8 0c0-2.3 1.1-5 4-7.3z" />
        <path d="M0 -7c2.9 2.3 4 5 4 7.3a4 4 0 0 1-8 0c0-2.3 1.1-5 4-7.3z" />
      </g>
      <g transform="translate(26 11) rotate(15)">
        <path {...ACCENT} stroke="none" d="M0 -7c2.9 2.3 4 5 4 7.3a4 4 0 0 1-8 0c0-2.3 1.1-5 4-7.3z" />
        <path d="M0 -7c2.9 2.3 4 5 4 7.3a4 4 0 0 1-8 0c0-2.3 1.1-5 4-7.3z" />
      </g>
      <g transform="translate(36 23) rotate(60)">
        <path {...ACCENT} stroke="none" d="M0 -7c2.9 2.3 4 5 4 7.3a4 4 0 0 1-8 0c0-2.3 1.1-5 4-7.3z" />
        <path d="M0 -7c2.9 2.3 4 5 4 7.3a4 4 0 0 1-8 0c0-2.3 1.1-5 4-7.3z" />
      </g>
      <g transform="translate(18 35) rotate(-70)">
        <path {...ACCENT} stroke="none" d="M0 -7c2.9 2.3 4 5 4 7.3a4 4 0 0 1-8 0c0-2.3 1.1-5 4-7.3z" />
        <path d="M0 -7c2.9 2.3 4 5 4 7.3a4 4 0 0 1-8 0c0-2.3 1.1-5 4-7.3z" />
      </g>
      <g transform="translate(31 38) rotate(25)">
        <path {...ACCENT} stroke="none" d="M0 -7c2.9 2.3 4 5 4 7.3a4 4 0 0 1-8 0c0-2.3 1.1-5 4-7.3z" />
        <path d="M0 -7c2.9 2.3 4 5 4 7.3a4 4 0 0 1-8 0c0-2.3 1.1-5 4-7.3z" />
      </g>
    </>
  ),
  sulphites: () => (
    <>
      <path {...ACCENT} stroke="none" d="M15.2 15h17.6c-.8 6.2-4.2 10-8.8 10s-8-3.8-8.8-10z" />
      <path d="M14 6h20c0 12-4 19-10 19S14 18 14 6z" />
      <path d="M24 25v16M16 42h16" />
    </>
  ),
  lupin: () => (
    <>
      <path {...ACCENT} stroke="none" d="M24 5c-4 5-7.2 11-7.2 17 0 3 3.2 5 7.2 5s7.2-2 7.2-5c0-6-3.2-12-7.2-17z" />
      <path d="M24 5c-4 5-7.2 11-7.2 17 0 3 3.2 5 7.2 5s7.2-2 7.2-5c0-6-3.2-12-7.2-17z" />
      <path d="M20 21.5l4 2.5 4-2.5M20.8 15.5l3.2 2 3.2-2M22.2 10l1.8 1.3 1.8-1.3" />
      <path d="M24 27v17M24 37l-9-4M24 37l-10.5 1.5M24 37l-8 5.5M24 37l9-4M24 37l10.5 1.5M24 37l8 5.5" />
    </>
  ),
  molluscs: () => (
    <>
      <path {...ACCENT} stroke="none" d="M24 39C14.5 36.5 7 29.5 7 21a17 17 0 0 1 34 0c0 8.5-7.5 15.5-17 18z" />
      <path d="M24 39C14.5 36.5 7 29.5 7 21a17 17 0 0 1 34 0c0 8.5-7.5 15.5-17 18z" />
      <path d="M24 39V5M24 39 13.5 8.5M24 39l10.5-30.5M24 39 8 15M24 39l16-24" />
      <path d="M19 37.5l-3.5 5.5h17L29 37.5" />
    </>
  ),
};

interface Props extends Omit<SVGProps<SVGSVGElement>, 'children'> {
  code: AllergenCode;
  /** Côté en px (le pictogramme est carré). */
  size?: number;
}

export default function AllergenIcon({ code, size = 14, ...rest }: Props) {
  return (
    <svg
      viewBox="0 0 48 48"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={3.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {SHAPES[code]()}
    </svg>
  );
}
